import React, { useState, useEffect } from "react";
import { ArrowRight, Gift, Check, Sparkles } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GiftCardPage = () => {
  const { user } = useAuth();
  const [denoms, setDenoms] = useState([25, 50, 100, 200, 500]);
  const [amount, setAmount] = useState(100);
  const [customAmount, setCustomAmount] = useState("");
  const [buyerEmail, setBuyerEmail] = useState("");
  const [buyerName, setBuyerName] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [personalMessage, setPersonalMessage] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/gift-cards/denominations`).then(({ data }) => {
      setDenoms(data?.amounts || [25, 50, 100, 200, 500]);
    }).catch(() => { /* keep defaults */ });
  }, []);

  useEffect(() => {
    if (user?.email && !buyerEmail) setBuyerEmail(user.email);
    if (user?.full_name && !buyerName) setBuyerName(user.full_name);
  }, [user]); // eslint-disable-line

  const finalAmount = customAmount ? parseInt(customAmount, 10) : amount;

  const submit = async (e) => {
    e.preventDefault();
    if (!finalAmount || finalAmount < 20 || finalAmount > 1000) {
      toast.error("Montant entre 20€ et 1000€");
      return;
    }
    if (!buyerEmail || !recipientEmail) {
      toast.error("Emails obligatoires");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/gift-cards/create-checkout`, {
        amount: finalAmount,
        buyer_email: buyerEmail,
        buyer_name: buyerName,
        recipient_email: recipientEmail,
        recipient_name: recipientName,
        personal_message: personalMessage,
        origin_url: window.location.origin,
      });
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error("Impossible de créer la session de paiement");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#050505] min-h-screen" data-testid="gift-card-page">
      <Navbar />

      {/* Hero */}
      <section className="pt-40 pb-16 md:pb-20 max-w-[1200px] mx-auto px-6 md:px-12">
        <div className="max-w-2xl">
          <p className="label-caps text-[color:var(--gold)] text-[10px] tracking-[0.3em] mb-6 flex items-center gap-2">
            <Sparkles size={12} strokeWidth={1.5} /> Un présent qui laisse une trace
          </p>
          <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl leading-[0.95] text-white mb-6">
            Offrez la <em className="italic text-[color:var(--gold-soft)]">rareté</em>
            <br /> d'une signature.
          </h1>
          <p className="text-white/60 font-light text-base md:text-lg leading-relaxed max-w-xl">
            Une carte cadeau MAISON DOUBA, livrée par email au moment que vous choisissez.
            Le destinataire pioche dans notre collection ce qui lui ressemble le plus.
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="max-w-[1200px] mx-auto px-6 md:px-12 pb-24">
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16">
          {/* Left: amount + preview */}
          <div>
            <p className="label-caps text-[color:var(--gold)] text-[10px] tracking-[0.3em] mb-4">Montant</p>
            <div className="grid grid-cols-3 gap-3 mb-4">
              {denoms.map((d) => (
                <button
                  type="button"
                  key={d}
                  data-testid={`gift-amount-${d}`}
                  onClick={() => { setAmount(d); setCustomAmount(""); }}
                  className={`py-5 px-3 border transition-all ${
                    !customAmount && amount === d
                      ? "border-[color:var(--gold)] bg-[color:var(--gold)]/[0.05] text-[color:var(--gold)]"
                      : "border-white/15 text-white/70 hover:border-white/35"
                  }`}
                >
                  <span className="font-serif text-2xl block">{d}€</span>
                </button>
              ))}
            </div>
            <div className="flex items-center gap-3 mb-8">
              <label className="text-[10px] label-caps text-white/40 whitespace-nowrap">Ou personnalisé :</label>
              <input
                data-testid="gift-amount-custom"
                type="number"
                min="20"
                max="1000"
                placeholder="20 - 1000"
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value)}
                className="input-line flex-1"
              />
              <span className="text-white/50 text-sm">€</span>
            </div>

            {/* Preview card */}
            <div className="border border-[color:var(--gold)]/30 bg-gradient-to-br from-[#0F0F0F] to-[#050505] p-8 relative overflow-hidden">
              <div className="absolute top-0 right-0 opacity-10">
                <Gift size={180} className="text-[color:var(--gold)]" strokeWidth={0.5} />
              </div>
              <p className="label-caps text-[color:var(--gold)] text-[9px] tracking-[0.3em] mb-3">
                Aperçu · Carte cadeau
              </p>
              <p className="font-serif text-6xl text-white mb-2">
                {finalAmount || 0}<span className="text-[color:var(--gold)] text-3xl align-top ml-1">€</span>
              </p>
              <p className="text-white/50 text-xs mb-6">Valable 12 mois · Toute la collection</p>
              {recipientName || recipientEmail ? (
                <p className="text-white/80 text-sm italic">
                  Pour <span className="text-[color:var(--gold)]">{recipientName || recipientEmail}</span>
                  {buyerName && <>, de la part de {buyerName}</>}
                </p>
              ) : (
                <p className="text-white/30 text-sm italic">Remplissez le formulaire →</p>
              )}
            </div>
          </div>

          {/* Right: recipient / buyer / message */}
          <div className="space-y-6">
            <div>
              <p className="label-caps text-[color:var(--gold)] text-[10px] tracking-[0.3em] mb-4">De la part de</p>
              <div className="space-y-4">
                <Field label="Votre nom" value={buyerName} onChange={setBuyerName} testid="gift-buyer-name" />
                <Field label="Votre email" value={buyerEmail} onChange={setBuyerEmail} testid="gift-buyer-email" required type="email" />
              </div>
            </div>
            <div>
              <p className="label-caps text-[color:var(--gold)] text-[10px] tracking-[0.3em] mb-4">Pour</p>
              <div className="space-y-4">
                <Field label="Prénom du destinataire" value={recipientName} onChange={setRecipientName} testid="gift-recipient-name" />
                <Field label="Email du destinataire" value={recipientEmail} onChange={setRecipientEmail} testid="gift-recipient-email" required type="email" />
              </div>
            </div>
            <div>
              <label className="block text-[10px] label-caps text-white/40 mb-2">Message personnel (optionnel)</label>
              <textarea
                data-testid="gift-message"
                value={personalMessage}
                onChange={(e) => setPersonalMessage(e.target.value.slice(0, 400))}
                rows={4}
                placeholder="Un mot doux à joindre à ce présent…"
                className="w-full bg-transparent border border-white/15 focus:border-[color:var(--gold)] p-3 text-white text-sm outline-none transition-colors"
              />
              <p className="text-white/30 text-[10px] mt-1">{personalMessage.length}/400</p>
            </div>
            <button
              data-testid="gift-submit"
              type="submit"
              disabled={loading || !finalAmount || !buyerEmail || !recipientEmail}
              className="btn-gold w-full justify-center py-4"
            >
              {loading ? "..." : (
                <>Offrir · {finalAmount || 0}€ <ArrowRight size={14} /></>
              )}
            </button>
            <p className="text-white/40 text-xs text-center">
              Paiement 100% sécurisé Stripe · La carte est délivrée par email dès la validation
            </p>
          </div>
        </form>
      </section>

      {/* How it works */}
      <section className="border-t border-white/10 py-20 max-w-[1200px] mx-auto px-6 md:px-12">
        <p className="label-caps text-[color:var(--gold)] text-[10px] tracking-[0.3em] mb-8 text-center">Comment ça marche</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { n: "01", t: "Choisissez le montant", d: "De 20€ à 1000€, à votre convenance." },
            { n: "02", t: "Recevez le code par email", d: "Immédiatement pour vous ou pour le destinataire." },
            { n: "03", t: "12 mois pour choisir", d: "Sur toute la collection, cumulable partiel." },
          ].map((s) => (
            <div key={s.n}>
              <p className="font-serif text-4xl text-[color:var(--gold)]/50 mb-3">{s.n}</p>
              <p className="text-white text-lg font-serif mb-2">{s.t}</p>
              <p className="text-white/50 text-sm leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
};

const Field = ({ label, value, onChange, testid, required, type = "text" }) => (
  <div>
    <label className="block text-[10px] label-caps text-white/40 mb-2">{label}</label>
    <input
      data-testid={testid}
      type={type}
      required={required}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="input-line w-full"
    />
  </div>
);

export default GiftCardPage;
